// src/components/SessionForm.js

import React, { useState } from "react";
import { Link, redirect } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const SessionForm = ({
  timeOfDay,
  dartsUsed,
  isDrunk,
  location,
  feeling,
  beerAmount,
  darts,
  onSaveSession,

  // Redux dispatch + actions
  dispatch,
  setTimeOfDay,
  setDartsUsed,
  setIsDrunk,
  setLocation,
  setFeeling,
  setBeerAmount,
  parentIsLoading = false,
  isSaveRateLimited = false,
  rateLimitRemainingTime = 0,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Track whether we're currently saving the session

  const [isSavingSession, setIsSavingSession] = useState(false);

  // Validate fields.
  // Adjust the required checks according to your needs.
  const isFormValid = () => {
    // For example, require timeOfDay, location, feeling, and a chosen dart
    // if (!timeOfDay) return false;
    // if (!location) return false;
    // if (!feeling) return false;
    // if (!dartsUsed) return false;
    // If isDrunk == true, you might want to ensure beerAmount is >= 1, etc. if that is required:
    // if (isDrunk && Number(beerAmount) <= 0) return false;
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if form is valid
    if (!isFormValid()) {
      alert(t("sessionForm.fillAllFields"));
      return;
    }

    // Check if save is blocked by loading or rate limiting
    if (parentIsLoading || isSaveRateLimited) {
      return; // Don't show alert if parent is already processing or rate limited
    }

    setIsSavingSession(true); // Disable the button
    try {
      await onSaveSession(); // calls the parent callback
    } finally {
      // Re-enable the button (on success or failure)
      setIsSavingSession(false);
      navigate("/history");
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-gradient-to-br from-secondary to-secondary/80 p-6 rounded-xl shadow-2xl border border-light/20 backdrop-blur-sm"
    >
      <h2 className="text-2xl font-bold mb-6 text-test text-center">
        {t("sessionForm.title")}
      </h2>

      {/* Time of the game */}
      {/* <div className="mb-4">
        <label className="block text-sm font-medium mb-1 text-white">
          {t("sessionForm.timeOfGame")}
        </label>
        <input
          type="time"
          value={timeOfDay}
          onChange={(e) => dispatch(setTimeOfDay(e.target.value))}
          className="w-full bg-primary border border-light text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-test"
        />
      </div> */}

      {/* Darts used */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-white">
            {t("sessionForm.dartsUsed")}
          </label>
          <Link to="/settings">
            <button className="text-orange-400 hover:text-orange-300 transition-colors duration-200">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </Link>
        </div>
        <div className="relative">
          <select
            value={dartsUsed}
            onChange={(e) => dispatch(setDartsUsed(e.target.value))}
            className="w-full bg-primary/80 border border-light/30 text-white text-sm rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-test transition-all duration-200"
          >
            <option value="">{t("sessionForm.selectDart")}</option>
            {darts.map((dart) => (
              <option key={dart.id} value={dart.id}>
                {dart.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Drunk / Sober */}
      <div className="mb-6 bg-primary/30 p-4 rounded-lg border border-light/20">
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={isDrunk}
            onChange={(e) => dispatch(setIsDrunk(e.target.checked))}
            className="h-5 w-5 text-test rounded accent-test focus:ring-2 focus:ring-test/50"
          />
          <label className="ml-3 text-sm font-medium text-white">
            {t("sessionForm.playedDrunk")}
          </label>
        </div>
      </div>

      {isDrunk && (
        <div className="mb-6 bg-primary/30 p-4 rounded-lg border border-light/20">
          <label className="block text-sm font-medium mb-2 text-white">
            {t("sessionForm.beerAmount")}
          </label>
          <input
            type="number"
            min="0"
            step="1"
            value={beerAmount}
            onChange={(e) => dispatch(setBeerAmount(e.target.value))}
            className="w-full bg-primary/80 border border-light/30 text-white text-sm rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-test transition-all duration-200"
            placeholder={t("sessionForm.beerPlaceholder")}
          />
        </div>
      )}

      {/* Location */}
      <div className="mb-6">
        <label className="block text-sm font-medium mb-2 text-white">
          {t("sessionForm.location")}
        </label>
        <select
          value={location}
          onChange={(e) => dispatch(setLocation(e.target.value))}
          className="w-full bg-primary/80 border border-light/30 text-white text-sm rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-test transition-all duration-200"
        >
          <option value="">{t("sessionForm.pleaseSelect")}</option>
          <option value="Home">{t("sessionForm.locations.home")}</option>
          <option value="Bar">{t("sessionForm.locations.bar")}</option>
          <option value="Club">{t("sessionForm.locations.club")}</option>
          <option value="Other">{t("sessionForm.locations.other")}</option>
        </select>
      </div>

      {/* Feeling */}
      <div className="mb-6">
        <label className="block text-sm font-medium mb-2 text-white">
          {t("sessionForm.howDidYouFeel")}
        </label>
        <div className="flex justify-between bg-primary/30 p-4 rounded-lg border border-light/20">
          <label className="flex flex-col items-center cursor-pointer group">
            <input
              type="radio"
              name="feeling"
              value="bad"
              checked={feeling === "bad"}
              onChange={(e) => dispatch(setFeeling(e.target.value))}
              className="h-4 w-4 text-test rounded accent-test focus:ring-2 focus:ring-test/50"
            />
            <span className="mt-2 text-white text-3xl group-hover:scale-110 transition-transform duration-200">
              😞
            </span>
            <span className="mt-1 text-xs text-white/80">
              {t("sessionForm.feelings.bad")}
            </span>
          </label>
          <label className="flex flex-col items-center cursor-pointer group">
            <input
              type="radio"
              name="feeling"
              value="okay"
              checked={feeling === "okay"}
              onChange={(e) => dispatch(setFeeling(e.target.value))}
              className="h-4 w-4 text-test rounded accent-test focus:ring-2 focus:ring-test/50"
            />
            <span className="mt-2 text-white text-3xl group-hover:scale-110 transition-transform duration-200">
              😐
            </span>
            <span className="mt-1 text-xs text-white/80">
              {t("sessionForm.feelings.okay")}
            </span>
          </label>
          <label className="flex flex-col items-center cursor-pointer group">
            <input
              type="radio"
              name="feeling"
              value="great"
              checked={feeling === "great"}
              onChange={(e) => dispatch(setFeeling(e.target.value))}
              className="h-4 w-4 text-test rounded accent-test focus:ring-2 focus:ring-test/50"
            />
            <span className="mt-2 text-white text-3xl group-hover:scale-110 transition-transform duration-200">
              😊
            </span>
            <span className="mt-1 text-xs text-white/80">
              {t("sessionForm.feelings.great")}
            </span>
          </label>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex flex-col items-center">
        {isSaveRateLimited && rateLimitRemainingTime > 0 && (
          <div className="mb-2 text-sm text-orange-400 text-center">
            {t("sessionForm.rateLimitWarning", {
              seconds: rateLimitRemainingTime,
            }) ||
              `Please wait ${rateLimitRemainingTime} seconds before saving again`}
          </div>
        )}
        <button
          type="submit"
          disabled={
            !isFormValid() ||
            isSavingSession ||
            parentIsLoading ||
            isSaveRateLimited
          }
          className="px-6 py-3 bg-gradient-to-r from-test to-test/80 text-primary font-medium rounded-full hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-test shadow-lg transition-all duration-200 transform hover:scale-105"
          style={
            !isFormValid() ||
            isSavingSession ||
            parentIsLoading ||
            isSaveRateLimited
              ? { opacity: 0.5, cursor: "not-allowed", transform: "scale(1)" }
              : {}
          }
        >
          {(() => {
            if (isSavingSession || parentIsLoading) {
              return t("sessionForm.saving") || "Saving...";
            }
            if (isSaveRateLimited) {
              return (
                t("sessionForm.rateLimited") ||
                `Wait ${rateLimitRemainingTime}s`
              );
            }
            return t("sessionForm.save") || "Save Session";
          })()}
        </button>
      </div>
    </form>
  );
};

export default SessionForm;
